import { useCallback, useMemo, useReducer, useRef, useState } from 'react';
import type {
  ResourcePickerCategory,
  ResourcePickerItem,
  ResourcePickerMode,
  ResourcePickerTab,
  VisibleRow,
} from './types';
import { ALL_TABS, TAB_CATEGORIES } from './types';
import { createResourcePickerFilter } from './utils/resource-picker-fuzzy-search';
import { readRecentIds, recordRecentId } from './utils/mention-recents';
import {
  buildIdleView,
  IDLE_PER_CATEGORY_CAP,
} from './utils/mention-idle-view';

// ─── State ──────────────────────────────────────────────────────────

type PickerState = {
  searchQuery: string;
  activeTab: ResourcePickerTab;
  cursorIndex: number;
  expandedProviders: Set<string>;
  /** Sections the user opened via "Show all", which then ignore the idle cap. */
  expandedCategories: Set<ResourcePickerCategory>;
  expandedItemId: string | undefined;
  selection: Set<string>;
};

type PickerAction =
  | { type: 'SET_SEARCH'; query: string }
  | { type: 'SET_TAB'; tab: ResourcePickerTab }
  | { type: 'SET_CURSOR'; index: number }
  | { type: 'MOVE_CURSOR'; delta: number; maxIndex: number }
  | { type: 'TOGGLE_PROVIDER'; provider: string }
  | { type: 'EXPAND_CATEGORY'; category: ResourcePickerCategory }
  | { type: 'SET_EXPANDED_ITEM'; id: string | undefined }
  | { type: 'TOGGLE_SELECTION'; id: string }
  | { type: 'CLEAR_SELECTION' }
  | { type: 'RESET' };

const initialState: PickerState = {
  searchQuery: '',
  activeTab: 'all',
  cursorIndex: 0,
  expandedProviders: new Set<string>(),
  expandedCategories: new Set<ResourcePickerCategory>(),
  expandedItemId: undefined,
  selection: new Set<string>(),
};

const reducer = (state: PickerState, action: PickerAction): PickerState => {
  switch (action.type) {
    case 'SET_SEARCH':
      return {
        ...state,
        searchQuery: action.query,
        cursorIndex: 0,
        // A query filters everything anyway, so a section left open from
        // browsing should not keep overriding the cap once you type.
        expandedCategories: new Set<ResourcePickerCategory>(),
      };
    case 'SET_TAB':
      return { ...state, activeTab: action.tab, cursorIndex: 0 };
    case 'EXPAND_CATEGORY': {
      const next = new Set(state.expandedCategories);
      next.add(action.category);
      return { ...state, expandedCategories: next };
    }
    case 'SET_CURSOR':
      return { ...state, cursorIndex: action.index };
    case 'MOVE_CURSOR': {
      if (action.maxIndex <= 0) {
        return state;
      }
      const next = state.cursorIndex + action.delta;
      const wrapped =
        next < 0 ? action.maxIndex - 1 : next >= action.maxIndex ? 0 : next;
      return { ...state, cursorIndex: wrapped };
    }
    case 'TOGGLE_PROVIDER': {
      const next = new Set(state.expandedProviders);
      if (next.has(action.provider)) {
        next.delete(action.provider);
      } else {
        next.add(action.provider);
      }
      return {
        ...state,
        expandedProviders: next,
        cursorIndex: state.cursorIndex,
      };
    }
    case 'SET_EXPANDED_ITEM':
      return { ...state, expandedItemId: action.id };
    case 'TOGGLE_SELECTION': {
      const next = new Set(state.selection);
      if (next.has(action.id)) {
        next.delete(action.id);
      } else {
        next.add(action.id);
      }
      return { ...state, selection: next };
    }
    case 'CLEAR_SELECTION':
      return { ...state, selection: new Set<string>() };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
};

// ─── Category group ordering + labels ───────────────────────────────

const CATEGORY_ORDER: ResourcePickerCategory[] = [
  'input',
  'agent',
  'workflow',
  'flow-control',
  'folder',
  'document',
  'dataset',
  'trigger',
  'tool',
];

const CATEGORY_LABELS: Record<ResourcePickerCategory, string> = {
  input: 'Inputs',
  agent: 'Agents',
  document: 'Documents',
  folder: 'Folders',
  trigger: 'Triggers',
  dataset: 'Datasets',
  tool: 'Tools',
  workflow: 'Workflows',
  'flow-control': 'Flow control',
};

// ─── Config ─────────────────────────────────────────────────────────

export type UseResourcePickerStateConfig<T> = {
  items: ResourcePickerItem<T>[];
  mode: ResourcePickerMode;
  initialTab?: ResourcePickerTab;
  /** Restrict which tabs appear. Defaults to all tabs. */
  availableTabs?: ResourcePickerTab[];
  /** Category icons keyed by category name (passed through to VisibleRow headers). */
  categoryIcons?: Partial<Record<ResourcePickerCategory, React.ReactNode>>;
  /** Provider icons keyed by provider id (shown on provider trigger rows). */
  providerIcons?: Map<string, React.ReactNode>;
  /**
   * Label for a trailing row that leaves the picker for the full catalogue.
   * Omit it and no such row is rendered.
   */
  browseAllLabel?: string;
  /**
   * Namespace for the recently-used list. Surfaces that mention different
   * things can keep separate histories; the default shares one.
   */
  recentScope?: string;
};

// ─── Return type ────────────────────────────────────────────────────

export type UseResourcePickerStateResult<T> = {
  /** Remembers an insertion so it leads the list next time. */
  recordRecentSelection: (id: string) => void;
  /** Lifts the idle cap on one section. */
  expandCategory: (category: ResourcePickerCategory) => void;
  // State
  searchQuery: string;
  activeTab: ResourcePickerTab;
  cursorIndex: number;
  expandedProviders: ReadonlySet<string>;
  expandedItemId: string | undefined;

  // Computed
  visibleRows: VisibleRow<T>[];
  /** The number of navigable (focusable) rows. */
  navCount: number;
  /** Tabs that have ≥ 1 matching item. Always includes 'all' if any tab has items. */
  activeTabs: ResourcePickerTab[];
  /** The set of selected item IDs (for multi-select). */
  selection: ReadonlySet<string>;
  selectionCount: number;
  selectedItems: ResourcePickerItem<T>[];

  // Actions
  setSearch: (query: string) => void;
  setTab: (tab: ResourcePickerTab) => void;
  setCursor: (index: number) => void;
  moveCursor: (delta: number) => void;
  toggleProvider: (provider: string) => void;
  setExpandedItem: (id: string | undefined) => void;
  toggleSelection: (id: string) => void;
  clearSelection: () => void;
  reset: () => void;

  /**
   * Keyboard handler. Returns `true` if the event was handled (caller should
   * `preventDefault`). Attach to the container's `onKeyDown` or pass to
   * SuggestionMenu's `beforeKeyDown`.
   */
  handleKeyDown: (event: KeyboardEvent | React.KeyboardEvent) => boolean;
};

// ─── Hook ───────────────────────────────────────────────────────────

export const useResourcePickerState = <T>(
  config: UseResourcePickerStateConfig<T>
): UseResourcePickerStateResult<T> => {
  const {
    items,
    mode,
    availableTabs,
    categoryIcons,
    providerIcons,
    recentScope = 'mention',
    browseAllLabel,
  } = config;

  // Recency is read once per mount and updated in place on selection, so the
  // list does not reshuffle under the cursor while the picker is open.
  const [recentIds, setRecentIds] = useState<string[]>(() =>
    readRecentIds(recentScope)
  );

  const expandCategory = useCallback((category: ResourcePickerCategory) => {
    dispatch({ type: 'EXPAND_CATEGORY', category });
  }, []);

  const recordRecentSelection = useCallback(
    (id: string) => {
      setRecentIds(recordRecentId(recentScope, id));
    },
    [recentScope]
  );

  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    activeTab: config.initialTab ?? 'all',
  });

  // ── Tab-filtered items ──
  const tabFilteredItems = useMemo(() => {
    const categories = TAB_CATEGORIES[state.activeTab];
    if (categories === 'all') {
      return items;
    }
    return items.filter((item) =>
      (categories as ResourcePickerCategory[]).includes(item.category)
    );
  }, [items, state.activeTab]);

  // ── Search-filtered items ──
  const searchFilteredItems = useMemo(() => {
    if (state.searchQuery.trim().length === 0) {
      return tabFilteredItems;
    }
    // Search across tab-filtered items only.
    const searchFn = createResourcePickerFilter(tabFilteredItems);
    return searchFn(state.searchQuery);
  }, [tabFilteredItems, state.searchQuery]);

  /**
   * With no query the list is just the catalog in category order, so whichever
   * category sorts first fills the screen. Lead with what was actually used
   * recently and show a sample of the rest.
   */
  const idleView = useMemo(
    () =>
      state.searchQuery.trim().length === 0
        ? buildIdleView(tabFilteredItems, recentIds, undefined, [
            'tool',
            ...state.expandedCategories,
          ])
        : null,
    [tabFilteredItems, recentIds, state.searchQuery, state.expandedCategories]
  );

  // ── Active tabs (tabs that have ≥ 1 item) ──
  const activeTabs = useMemo(() => {
    const tabsToCheck = availableTabs ?? ALL_TABS;
    const result: ResourcePickerTab[] = [];
    for (const tab of tabsToCheck) {
      if (tab === 'all') {
        // 'all' tab is always shown if there are any items at all
        if (items.length > 0) {
          result.push(tab);
        }
        continue;
      }
      const categories = TAB_CATEGORIES[tab];
      if (categories === 'all') {
        if (items.length > 0) {
          result.push(tab);
        }
        continue;
      }
      const hasItems = items.some((item) =>
        (categories as ResourcePickerCategory[]).includes(item.category)
      );
      if (hasItems) {
        result.push(tab);
      }
    }
    return result;
  }, [items, availableTabs]);

  // ── Build visible rows ──
  const { visibleRows, navCount } = useMemo(() => {
    const rows: VisibleRow<T>[] = [];
    let navIdx = 0;
    const hasSearchQuery = state.searchQuery.trim().length > 0;
    const autoExpandToolProviders = state.searchQuery.trim().length > 2;

    // Recents lead the idle list. They keep their own section rather than being
    // reordered inside their category, so the shortcut is visible as a band
    // instead of being buried in whichever group happens to sort first.
    if (idleView !== null && idleView.recent.length > 0) {
      rows.push({ kind: 'group-header', label: 'Recent' });
      for (const item of idleView.recent) {
        rows.push({
          kind: 'item',
          item,
          indented: false,
          showTypeLabel: true,
          navIndex: navIdx,
        });
        navIdx += 1;
      }
    }

    const groupSource =
      idleView !== null ? idleView.byCategory : searchFilteredItems;

    // Group items by category, preserving CATEGORY_ORDER.
    const byCategory = new Map<
      ResourcePickerCategory,
      ResourcePickerItem<T>[]
    >();
    for (const item of groupSource) {
      const existing = byCategory.get(item.category);
      if (existing !== undefined) {
        existing.push(item);
      } else {
        byCategory.set(item.category, [item]);
      }
    }

    for (const category of CATEGORY_ORDER) {
      const categoryItems = byCategory.get(category);
      if (categoryItems === undefined || categoryItems.length === 0) {
        continue;
      }

      // Add group header (non-navigable) when multiple categories are visible,
      // so items are always visually grouped regardless of which tab is active.
      if (byCategory.size > 1) {
        rows.push({
          kind: 'group-header',
          category,
          label: CATEGORY_LABELS[category],
          icon: categoryIcons?.[category],
        });
      }

      // Tools are special: group by provider with expand/collapse.
      if (category === 'tool') {
        const byProvider = new Map<string, ResourcePickerItem<T>[]>();
        const providerOrder: string[] = [];
        for (const item of categoryItems) {
          const key = item.provider ?? '__other__';
          const existing = byProvider.get(key);
          if (existing !== undefined) {
            existing.push(item);
          } else {
            byProvider.set(key, [item]);
            providerOrder.push(key);
          }
        }

        if (hasSearchQuery === false) {
          providerOrder.sort((a, b) => {
            const aLabel = byProvider.get(a)?.[0]?.providerLabel ?? a;
            const bLabel = byProvider.get(b)?.[0]?.providerLabel ?? b;
            return aLabel.localeCompare(bLabel);
          });
        }

        const providerCap =
          idleView !== null && !state.expandedCategories.has('tool')
            ? IDLE_PER_CATEGORY_CAP
            : providerOrder.length;
        const shownProviders = providerOrder.slice(0, providerCap);

        for (const providerKey of shownProviders) {
          const providerItems = byProvider.get(providerKey)!;
          const expanded =
            autoExpandToolProviders || state.expandedProviders.has(providerKey);
          const someAvailable = providerItems.some(
            (item) => item.available === true
          );

          rows.push({
            kind: 'provider-trigger',
            provider: providerKey,
            label: providerItems[0]?.providerLabel ?? providerKey,
            icon: providerIcons?.get(providerKey),
            toolCount: providerItems.length,
            expanded,
            someAvailable,
            navIndex: navIdx,
          });
          navIdx += 1;

          if (expanded) {
            for (const item of providerItems) {
              rows.push({
                kind: 'item',
                item,
                indented: true,
                navIndex: navIdx,
              });
              navIdx += 1;
            }
          }
        }

        if (shownProviders.length < providerOrder.length) {
          rows.push({
            kind: 'show-more',
            category: 'tool',
            label: `Show all ${providerOrder.length} providers`,
            navIndex: navIdx,
          });
          navIdx += 1;
        }
      } else {
        // All other categories: flat list of items.
        for (const item of categoryItems) {
          rows.push({
            kind: 'item',
            item,
            indented: false,
            navIndex: navIdx,
          });
          navIdx += 1;
        }
        const cut = idleView?.truncated.find((t) => t.category === category);
        if (cut !== undefined) {
          rows.push({
            kind: 'show-more',
            category,
            label: `Show all ${cut.total} ${CATEGORY_LABELS[
              category
            ].toLowerCase()}`,
            navIndex: navIdx,
          });
          navIdx += 1;
        }
      }
    }

    if (browseAllLabel !== undefined) {
      rows.push({
        kind: 'browse-all',
        label: browseAllLabel,
        navIndex: navIdx,
      });
      navIdx += 1;
    }

    return { visibleRows: rows, navCount: navIdx };
  }, [
    searchFilteredItems,
    idleView,
    state.expandedCategories,
    state.searchQuery,
    state.expandedProviders,
    categoryIcons,
    providerIcons,
    browseAllLabel,
  ]);

  // ── Selection ──
  const selectedItems = useMemo(
    () => items.filter((item) => state.selection.has(item.id)),
    [items, state.selection]
  );

  // ── Actions ──
  const setSearch = useCallback(
    (query: string) => dispatch({ type: 'SET_SEARCH', query }),
    []
  );
  const setTab = useCallback(
    (tab: ResourcePickerTab) => dispatch({ type: 'SET_TAB', tab }),
    []
  );
  const setCursor = useCallback(
    (index: number) => dispatch({ type: 'SET_CURSOR', index }),
    []
  );
  const moveCursor = useCallback(
    (delta: number) =>
      dispatch({ type: 'MOVE_CURSOR', delta, maxIndex: navCount }),
    [navCount]
  );
  const toggleProvider = useCallback(
    (provider: string) => dispatch({ type: 'TOGGLE_PROVIDER', provider }),
    []
  );
  const setExpandedItem = useCallback(
    (id: string | undefined) => dispatch({ type: 'SET_EXPANDED_ITEM', id }),
    []
  );
  const toggleSelection = useCallback(
    (id: string) => dispatch({ type: 'TOGGLE_SELECTION', id }),
    []
  );
  const clearSelection = useCallback(
    () => dispatch({ type: 'CLEAR_SELECTION' }),
    []
  );
  const reset = useCallback(() => dispatch({ type: 'RESET' }), []);

  // ── Stable ref to latest navCount for the keyboard handler ──
  const navCountRef = useRef(navCount);
  navCountRef.current = navCount;

  // ── Stable ref to latest visibleRows for keyboard handler ──
  const visibleRowsRef = useRef(visibleRows);
  visibleRowsRef.current = visibleRows;

  // ── Stable ref to latest state for keyboard handler ──
  const stateRef = useRef(state);
  stateRef.current = state;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent | React.KeyboardEvent): boolean => {
      const { key } = event;

      switch (key) {
        case 'ArrowDown': {
          dispatch({
            type: 'MOVE_CURSOR',
            delta: 1,
            maxIndex: navCountRef.current,
          });
          return true;
        }
        case 'ArrowUp': {
          dispatch({
            type: 'MOVE_CURSOR',
            delta: -1,
            maxIndex: navCountRef.current,
          });
          return true;
        }
        case 'Enter': {
          // Find the navigable row at the current cursor.
          const currentRows = visibleRowsRef.current;
          const currentState = stateRef.current;
          const focusedRow = currentRows.find((row) => {
            if (row.kind === 'item') {
              return row.navIndex === currentState.cursorIndex;
            }
            if (row.kind === 'provider-trigger') {
              return row.navIndex === currentState.cursorIndex;
            }
            return false;
          });

          if (focusedRow === undefined) {
            return false;
          }

          if (focusedRow.kind === 'provider-trigger') {
            dispatch({
              type: 'TOGGLE_PROVIDER',
              provider: focusedRow.provider,
            });
            return true;
          }

          // For items, the caller handles selection via onSelect callback.
          // Return true to signal the event was handled, but actual selection
          // is done by the rendering component which calls onSelect/toggleSelection.
          return true;
        }
        case 'ArrowRight': {
          const currentRows = visibleRowsRef.current;
          const currentState = stateRef.current;
          const focusedRow = currentRows.find(
            (row) =>
              (row.kind === 'provider-trigger' || row.kind === 'item') &&
              row.navIndex === currentState.cursorIndex
          );
          if (
            focusedRow !== undefined &&
            focusedRow.kind === 'provider-trigger' &&
            !focusedRow.expanded
          ) {
            dispatch({
              type: 'TOGGLE_PROVIDER',
              provider: focusedRow.provider,
            });
            return true;
          }
          return false;
        }
        case 'ArrowLeft': {
          const currentRows = visibleRowsRef.current;
          const currentState = stateRef.current;
          const focusedRow = currentRows.find(
            (row) =>
              (row.kind === 'provider-trigger' || row.kind === 'item') &&
              row.navIndex === currentState.cursorIndex
          );
          // If on a child tool (indented), collapse its parent provider
          if (
            focusedRow !== undefined &&
            focusedRow.kind === 'item' &&
            focusedRow.indented === true &&
            focusedRow.item.provider !== undefined
          ) {
            dispatch({
              type: 'TOGGLE_PROVIDER',
              provider: focusedRow.item.provider,
            });
            return true;
          }
          // If on an expanded provider, collapse it
          if (
            focusedRow !== undefined &&
            focusedRow.kind === 'provider-trigger' &&
            focusedRow.expanded
          ) {
            dispatch({
              type: 'TOGGLE_PROVIDER',
              provider: focusedRow.provider,
            });
            return true;
          }
          return false;
        }
        case ' ': {
          // Space toggles selection in multi-select mode.
          if (mode !== 'multi') {
            return false;
          }
          const currentRows = visibleRowsRef.current;
          const currentState = stateRef.current;
          const focusedRow = currentRows.find(
            (row) =>
              row.kind === 'item' && row.navIndex === currentState.cursorIndex
          );
          if (focusedRow !== undefined && focusedRow.kind === 'item') {
            dispatch({
              type: 'TOGGLE_SELECTION',
              id: focusedRow.item.id,
            });
            return true;
          }
          return false;
        }
        case 'Tab': {
          // Cycle through tabs.
          const currentState = stateRef.current;
          const currentTabIndex = activeTabs.indexOf(currentState.activeTab);
          const shiftKey = (event as KeyboardEvent).shiftKey;
          const nextIndex = shiftKey
            ? (currentTabIndex - 1 + activeTabs.length) % activeTabs.length
            : (currentTabIndex + 1) % activeTabs.length;
          const nextTab = activeTabs[nextIndex];
          if (nextTab !== undefined) {
            dispatch({ type: 'SET_TAB', tab: nextTab });
          }
          return true;
        }
        default:
          return false;
      }
    },
    [mode, activeTabs]
  );

  return {
    recordRecentSelection,
    expandCategory,
    searchQuery: state.searchQuery,
    activeTab: state.activeTab,
    cursorIndex: state.cursorIndex,
    expandedProviders: state.expandedProviders,
    expandedItemId: state.expandedItemId,
    visibleRows,
    navCount,
    activeTabs,
    selection: state.selection,
    selectionCount: state.selection.size,
    selectedItems,
    setSearch,
    setTab,
    setCursor,
    moveCursor,
    toggleProvider,
    setExpandedItem,
    toggleSelection,
    clearSelection,
    reset,
    handleKeyDown,
  };
};
